import React from 'react';
import { Activity, Zap, TrendingDown, RotateCcw, AlertTriangle, History, ShieldCheck, Target } from 'lucide-react';
import type { CompanyAnalysis } from '../../types/geminiTypes';
import { cn, coerceToText } from '../../lib/utils';
import { safeToFixed } from '../../lib/formatters';
import { OracleDetailSection } from './OracleDetailSection';
import ReactMarkdown from 'react-markdown';
import { Tooltip } from '../Tooltip';

export interface RiskTabProps {
  analysis: CompanyAnalysis;
}

export function RiskTab({ analysis }: RiskTabProps) {
  return (
    <div className="space-y-8">
      {analysis.oracleFramework && (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="oi-card p-4 md:p-8">
                        <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2 flex items-center gap-2">
                          <Activity className="w-3 h-3" />
                          Signal Volatility
                        </div>
                        <div className="text-3xl font-mono font-medium text-ink mb-1">
                          {analysis.volatilityScore || 0}%
                        </div>
                        <div className="text-[10px] text-ink-500 uppercase tracking-wider font-medium">
                          {analysis.volatilityScore && analysis.volatilityScore > 70 ? 'High Noise / Low Confidence' : 'Stable Signals'}
                        </div>
                      </div>
                      <div className="oi-card p-4 md:p-8">
                        <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2 flex items-center gap-2">
                          <Zap className="w-3 h-3" />
                          Monetization Proof
                        </div>
                        <div className="text-3xl font-mono font-medium text-ink mb-1">
                          {analysis.newRevenueContribution || 0}%
                        </div>
                        <div className="text-[10px] text-ink-500 uppercase tracking-wider font-medium">
                          New Revenue Contribution
                        </div>
                      </div>
                      <div className="oi-card p-4 md:p-8">
                        <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2 flex items-center gap-2">
                          <TrendingDown className="w-3 h-3" />
                          Deceleration Risk
                        </div>
                        <div className="text-3xl font-mono font-medium text-ink mb-1">
                          {analysis.isDecelerating ? 'YES' : 'NO'}
                        </div>
                        <div className="text-[10px] text-ink-500 uppercase tracking-wider font-medium">
                          2+ Quarters of Slowdown
                        </div>
                      </div>
                      <div className="oi-card p-4 md:p-8">
                        <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2 flex items-center gap-2">
                          <RotateCcw className="w-3 h-3" />
                          Re-Inflection
                        </div>
                        <div className="text-3xl font-mono font-medium text-ink mb-1">
                          {analysis.isReInflection ? 'YES' : 'NO'}
                        </div>
                        <div className="text-[10px] text-ink-500 uppercase tracking-wider font-medium">
                          Turnaround Potential
                        </div>
                      </div>
                    </div>
                  </div>
                )}
      {analysis.riskModeling && analysis.riskModeling.length > 0 && (
                <div className="mb-12">
                  <div className="oi-card p-5 md:p-10">
                    <div className="mb-8 pb-4 border-b border-ink-200">
                      <h3 className="oi-section-title">Quantitative Risk Modeling</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-ink-200 text-[10px] font-medium uppercase tracking-widest text-ink-500">
                            <th className="py-6 pr-6">Core Risk Factor</th>
                            <th className="py-6 px-6">Probability (%)</th>
                            <th className="py-6 px-6">Impact Severity</th>
                            <th className="py-6 pl-6 text-right">Potential IV Change</th>
                            <th className="py-6 px-6">IV $ Impact / Share</th>
                            <th className="py-6 pl-6 text-right">Risk-Adjusted IV Floor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-200">
                          {analysis.riskModeling.map((item, idx) => (
                            <tr key={idx} className="group hover:bg-surface-2 transition-colors">
                              <td className="py-6 pr-6">
                                <div className="text-sm font-medium text-ink mb-1">{item.risk}</div>
                              </td>
                              <td className="py-6 px-6">
                                <div className="text-sm font-mono font-medium text-ink-600 bg-surface-2 px-3 py-1 rounded-md inline-block">{item.probability}</div>
                              </td>
                              <td className="py-6 px-6">
                                <div className={cn(
                                  "px-3 py-1 rounded-md text-[9px] font-medium uppercase tracking-widest inline-block",
                                  item.impact.toLowerCase().includes('high') ? "bg-danger-600 text-paper" :
                                  item.impact.toLowerCase().includes('med') ? "bg-warning-600 text-paper" :
                                  "bg-success-600 text-paper"
                                )}>
                                  {item.impact}
                                </div>
                              </td>
                              <td className="py-6 pl-6 text-right">
                                <div className="text-sm font-mono font-medium text-danger-600">{item.ivChange}</div>
                              </td>
                              <td className="py-6 px-6">
                                <div className="text-sm font-mono font-medium text-danger-600">{item.ivImpactPerShare}</div>
                              </td>
                              <td className="py-6 pl-6 text-right">
                                <div className="text-sm font-mono font-medium text-ink">{item.riskAdjustedIV}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
      {(analysis.historicalFailuresContext || analysis.technicalDifferentiation) && (
                <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {analysis.historicalFailuresContext && (
                    <div className="oi-card p-4 md:p-8">
                      <div className="flex items-center gap-3 mb-4">
                        <History className="w-5 h-5 text-oxblood" />
                        <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500">Historical Failures & Context</h4>
                      </div>
                      <p className="text-sm text-ink-600 leading-relaxed">
                        {coerceToText(analysis.historicalFailuresContext)}
                      </p>
                    </div>
                  )}
                  {analysis.technicalDifferentiation && (
                    <div className="oi-card p-4 md:p-8">
                      <div className="flex items-center gap-3 mb-4">
                        <ShieldCheck className="w-5 h-5 text-oxblood" />
                        <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500">Technical Differentiation</h4>
                      </div>
                      <p className="text-sm text-ink-600 leading-relaxed">
                        {coerceToText(analysis.technicalDifferentiation)}
                      </p>
                    </div>
                  )}
                </div>
              )}
      {analysis.whyNotPerfect && (
                <div className="mb-8 p-4 md:p-8 tone-danger rounded-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-danger-600" />
                    <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] opacity-70">Why this is NOT a perfect business</h4>
                  </div>
                  <p className="text-sm opacity-90 leading-relaxed italic">
                    {coerceToText(analysis.whyNotPerfect)}
                  </p>
                </div>
              )}
      {analysis.tenYearHistoricalContext && (
                <div className="mb-8 oi-card p-4 md:p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <History className="w-5 h-5 text-oxblood" />
                      <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500">10-Year Historical Context</h4>
                    </div>
                    <p className="text-sm text-ink-600 leading-relaxed">
                      {coerceToText(analysis.tenYearHistoricalContext)}
                    </p>
                  </div>
                )}
    </div>
  );
}
